<template>
  <div class="creator-spotlight mb-3">
    <div
      class="creator-spotlight__background position-absolute"
      :style="{ background: backgroundValue }"
    />
    <div class="creator-spotlight__container" @click="$emit('click', creator)">
      <div class="creator-spotlight__info">
        <div class="creator-spotlight__info-title">
          <span :style="titleStyles">{{
            $get(creator, 'profile.handle')
          }}</span>
          <CreatorCheckmark :color="background" class="ml-1" />
        </div>
        <div ref="descriptionInfo" class="creator-spotlight__info-description">
          {{ descriptionInfo }}
        </div>
        <CreatorLinks
          v-if="$get(creator, 'platforms', []).length"
          class="mt-auto creator-spotlight__links"
          source-type="creator"
          :data="creator"
        />
      </div>
      <CloudinaryImage
        id="creator-spotlight"
        :url="getPreviewImage()"
        alt="creator-spotlight"
        :width="150"
        :height="175"
        :modifiers="{ gravity: 'face', roundCorner: '5' }"
        class="creator-spotlight__img"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import CreatorLinks from '../CreatorLinks';
import CreatorCheckmark from '../Creators/CreatorCheckmark.vue';
import CloudinaryImage from '@/components/Elements/Image';
import { FallbackImageURL, ImageTypes } from '@/misc/constants';

export default {
  name: 'CreatorSpotlight',
  components: { CreatorLinks, CreatorCheckmark, CloudinaryImage },
  props: {
    title: {
      type: String,
      default: null
    },
    creator: {
      type: Object,
      default: () => {}
    },
    background: {
      type: String,
      default: 'transparent'
    }
  },
  data() {
    return {
      descriptionWidth: 0
    };
  },
  computed: {
    ...mapState('main', ['windowWidth']),
    backgroundValue() {
      if (!process.client) return 'transparent';

      return this.background;
    },
    titleStyles() {
      if (this.windowWidth < 391) {
        if (this.$get(this.creator, 'profile.handle.length') > 15)
          return { fontSize: '13px' };
      }
      if (this.windowWidth < 430) {
        if (this.$get(this.creator, 'profile.handle.length') > 15)
          return { fontSize: '14px', letterSpacing: '-0.5px' };
        return { fontSize: '15px', letterSpacing: '-0.5px' };
      }
      if (this.windowWidth < 480) {
        if (this.$get(this.creator, 'profile.handle.length') > 15)
          return { fontSize: '15px' };
        if (this.$get(this.creator, 'profile.handle.length') > 20)
          return { fontSize: '14px', letterSpacing: '-0.5px' };
        return { fontSize: '17px' };
      }
      if (this.windowWidth < 768) return { fontSize: '20px' };
      return { fontSize: '24px' };
    },
    descriptionInfo() {
      if (!this.descriptionWidth) return;
      const description = this.$get(this.creator, 'profile.shortDescription');
      const averageCharWidth = 9;
      const maxLenth = (this.descriptionWidth * 5) / averageCharWidth;
      if (description.length > maxLenth) {
        return description.substr(0, maxLenth - 3).trim() + '...';
      }
      return description;
    }
  },
  mounted() {
    this.trackDescriptionWidth();
    window.addEventListener('resize', this.trackDescriptionWidth);
  },
  destroyed() {
    window.removeEventListener('resize', this.trackDescriptionWidth);
  },
  methods: {
    getPreviewImage() {
      const creatorPhotos = this.$get(this.creator, 'profile.photos', []);
      let photo = null;
      if (creatorPhotos.length) {
        photo = creatorPhotos.find(
          (image) => image.role === ImageTypes.Main
        )?.imageUrl;
      }
      return photo || FallbackImageURL;
    },
    trackDescriptionWidth() {
      this.descriptionWidth = this.$refs.descriptionInfo.clientWidth;
    }
  }
};
</script>

<style lang="scss" scoped>
.creator-spotlight {
  position: relative;
  width: 100%;
  border-radius: 10px;
  cursor: pointer;

  @media (min-width: $screen-width-lg) {
    width: 49%;
  }

  &__background {
    position: absolute;
    width: 100%;
    height: 200px;
    border-radius: 10px;
    transform: rotate(-1.5deg);
  }

  &__container {
    display: flex;
    position: relative;
    height: 200px;
    border-radius: 10px;
    background: $dark-secondary-color;
    background-repeat: no-repeat;
  }

  &__img {
    border-radius: 5px;
    margin: 13px 12px 12px auto;
    width: 150px;
    height: 175px;
    margin-left: 10px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 30px 0 20px 15px;
    word-break: break-word;

    &-title {
      display: flex;
      align-items: center;
      font-family: 'Poppins-SemiBold', sans-serif;
      line-height: 1.1;
      padding-bottom: 20px;
    }

    &-description {
      font-size: 12px;
      line-height: 16px;
    }
  }

  @media (max-width: $screen-width-md) {
    &__links::v-deep {
      img {
        margin-left: 0 !important;
        margin-right: 10px !important;
      }
      span {
        margin-right: 7px !important;
      }
    }
  }
}
</style>
